import { addDays, addYears, differenceInCalendarDays, getDate, isValid, parse } from 'date-fns';

import { formatLocalDate } from '../../shared/utils/date-time/date-time.utils';
import { PatientPortalAccessUser, UserPatientStatusType } from '../models/patient-portal-access';

/**
 * Age of majority. Mirrors `PatientAgeUtility.AdultAgeYears` in
 * `MedOne.Conduit.Function.PatientPortalUsers`, which is the job that actually performs the
 * revocation, and `AdultAgeYears` in the admin API behind the Minor badge on the proxy queue.
 * Change all three or none — otherwise this screen counts down to a date the job doesn't act on.
 */
export const ADULT_AGE_YEARS = 18;

/**
 * How close the patient's 18th birthday has to be before their proxy rows are flagged.
 *
 * A month: long enough that staff meet the warning while there is still time to tell a caregiver
 * their access is ending, short enough that it isn't standing on rows that age out next year — a
 * warning that is always on is one nobody reads.
 */
export const PROXY_AGE_OUT_WARNING_DAYS = 30;

/**
 * Where a grant stands relative to the patient's 18th birthday.
 *
 * One discriminant rather than a pair of booleans: "overdue" implies "aging out", and a pair would
 * leave every caller re-deriving the exclusion (`isAgingOut && !isOverdue`) to pick a treatment.
 *
 * - `none` — nothing is ending: an adult patient, a revoked link, or a birthday still far off.
 * - `soon` — inside the warning window, ending on a known date.
 * - `overdue` — the patient is already an adult and the link is **still live**. The revoking job's
 *   catch-up window has passed, so nothing will close it but a staff member.
 */
export type ProxyAgeOut = 'none' | 'soon' | 'overdue';

/** The date line under a proxy's name, split so the urgent part can be emphasised. */
export interface ProxyTimeline {
  /**
   * Always rendered, and always opens with "Linked <date>" — that date is the row's Date Added, so an
   * age-out countdown is appended to it rather than shown in its place. Ends mid-sentence when
   * {@link emphasis} continues it.
   */
  text: string;
  /** The part rendered in bold, or null when the whole line is plain. */
  emphasis: string | null;
  /** See {@link ProxyAgeOut}. */
  ageOut: ProxyAgeOut;
}

/**
 * A `yyyy-MM-dd` (or the date half of a timestamp) as a local calendar day.
 *
 * Parsed rather than passed through `new Date()`: the spec says a bare "2008-03-14" is UTC
 * midnight, which is March 13 to every zone west of Greenwich — and a birthday off by a day is a
 * wrong answer to give a caregiver about when their access ends.
 */
function toCalendarDate(value: string, reference: Date): Date {
  return parse(value.slice(0, 10), 'yyyy-MM-dd', reference);
}

/**
 * The first day the patient counts as an adult, or null when the date of birth is unreadable.
 *
 * The 29 February case is handled the way the job handles it, not the way `addYears` does on its
 * own. The job's test is `dateOfBirth <= today.AddYears(-18)`, and `DateOnly.AddYears` clamps a
 * 29 February to the 28th — so a patient born 29 February 2008 is still a minor on 28 February
 * 2026 and comes of age on 1 March. `addYears` here clamps the same way, so the clamp is detected
 * (the day of the month changed) and the day added back.
 */
export function getAdulthoodDate(dateOfBirth: string, reference: Date = new Date()): Date | null {
  const birth = toCalendarDate(dateOfBirth, reference);
  if (!isValid(birth)) {
    return null;
  }

  const adulthood = addYears(birth, ADULT_AGE_YEARS);
  return getDate(adulthood) === getDate(birth) ? adulthood : addDays(adulthood, 1);
}

/**
 * The date this link ages out on, or null when the minor-aging job will never revoke it.
 *
 * Three conditions, all taken from `GetAgedOutProxyLinksQuery`:
 *
 * 1. The link is **active** — the job only touches live links.
 * 2. It is **not the patient's own** (Self) link. Note the job scopes on that alone, not on the
 *    relationship being parental: a Spouse or Caregiver link on a minor ages out too. Narrowing
 *    this to Parent/Guardian would leave staff unwarned about access that is in fact ending.
 * 3. The patient was **still a minor when the link was created** (`dateOfBirth >
 *    createdOn.AddYears(-18)`). This is what keeps an ordinary adult's spouse or caregiver link
 *    from being counted down to a birthday that has no bearing on it.
 *
 * The creation date is compared as the UTC calendar day the job reads it as
 * (`DateOnly.FromDateTime(CreatedOnUtc)`), not as the viewer's local day.
 */
function resolveAgeOutDate(
  user: PatientPortalAccessUser,
  dateOfBirth: string,
  reference: Date,
): Date | null {
  if (!isProxyLink(user) || !isActiveLink(user)) {
    return null;
  }

  const adulthood = getAdulthoodDate(dateOfBirth, reference);
  if (!adulthood) {
    return null;
  }

  const linkedOn = toCalendarDate(user.createdOnUtc, reference);
  return isValid(linkedOn) && adulthood > linkedOn ? adulthood : null;
}

/**
 * Whether this link belongs on the Proxy / Caregiver Access card.
 *
 * The patient's own (Self) account comes back in the same `linkedUsers` list, and listing it as a
 * caregiver would have staff revoking a patient's access to their own record from a card that
 * doesn't say that is what it does.
 */
export function isProxyLink(user: PatientPortalAccessUser): boolean {
  return user.isProxy;
}

/** Whether the link currently grants access. */
export function isActiveLink(user: PatientPortalAccessUser): boolean {
  return user.userPatientStatusTypeId === UserPatientStatusType.Active;
}

/** "14 days", "1 day" — so the line reads as a sentence at every distance. */
function formatDaysRemaining(days: number): string {
  return days === 1 ? '1 day' : `${days} days`;
}

/**
 * What is happening (or has already happened) to one grant, in one line.
 *
 * The age-out is a fact about **the patient**, not about the link: nothing on a
 * `PatientPortalAccessUser` carries an expiry, because there isn't one — the link lives until the
 * patient turns 18 and a nightly job revokes it. So the countdown is computed from the patient's
 * date of birth, and only for the links that job will actually act on (see
 * {@link resolveAgeOutDate}).
 *
 * Counted in calendar days rather than elapsed hours: "auto-revokes in 14 days" is a claim about
 * dates on a calendar, and a grant ending tomorrow morning should not read as "in 0 days" because
 * fewer than 24 hours remain.
 *
 * `now` is a parameter so the caller's clock is explicit and the specs need no fake timers.
 */
export function describeProxyTimeline(
  user: PatientPortalAccessUser,
  dateOfBirth: string | null | undefined,
  now: Date = new Date(),
): ProxyTimeline {
  const linked = `Linked ${formatLocalDate(user.createdOnUtc)}`;
  const plain: ProxyTimeline = {
    text: linked,
    emphasis: null,
    ageOut: 'none',
  };

  // No date of birth, no age-out: the row keeps its linked-on line rather than counting down
  // from a guess.
  if (!dateOfBirth) {
    return plain;
  }

  const adulthood = resolveAgeOutDate(user, dateOfBirth, now);
  if (!adulthood) {
    return plain;
  }

  const daysRemaining = differenceInCalendarDays(adulthood, now);

  /**
   * The patient is already an adult and the link is still live. The job's catch-up window is
   * seven days (`MinorAgingCatchUpDays`), so a link it missed is stranded active for good — and
   * this screen is the only place a staff member would ever see that. It is told apart from
   * "ending soon" rather than folded into it, because nothing is going to happen on its own.
   */
  if (daysRemaining < 0) {
    return {
      text: `${linked} · Patient turned ${ADULT_AGE_YEARS} on ${formatLocalDate(adulthood)} — `,
      emphasis: 'access still active',
      ageOut: 'overdue',
    };
  }

  if (daysRemaining === 0) {
    return {
      text: `${linked} · Access auto-revokes `,
      emphasis: 'today',
      ageOut: 'soon',
    };
  }

  if (daysRemaining <= PROXY_AGE_OUT_WARNING_DAYS) {
    return {
      text: `${linked} · Access auto-revokes in `,
      emphasis: formatDaysRemaining(daysRemaining),
      ageOut: 'soon',
    };
  }

  // Far enough out to be a date rather than a countdown, but still worth stating: "until he turns
  // 18" is the thing a caregiver rings up about.
  return { ...plain, text: `${linked} · Auto-revokes ${formatLocalDate(adulthood)}` };
}

/**
 * Whether Revoke is offered on this row. Only an active proxy link can be withdrawn — offering it
 * on an already-revoked row invites a staff member to "fix" something that is already closed, and
 * the API rejects a revoke on the Self link outright.
 */
export function canRevokeProxy(user: PatientPortalAccessUser): boolean {
  return isProxyLink(user) && isActiveLink(user);
}
